import { useField } from 'formik'
import React, { useCallback, useMemo } from 'react'

import multiItemSelect, { CreateItemSelectArgs, MultiItemSelectProps } from './multiItemSelect'

/**********************************************************************
 * singleItemSelect
 **********************************************************************/
export type SingleItemSelectProps<T extends { uuid: string }> = {
  item: T | null | undefined
  setItem: React.Dispatch<React.SetStateAction<T | null | undefined>>
} & Omit<MultiItemSelectProps<T>, 'items' | 'setItems'>

export default function singleItemSelect<T extends { uuid: string }>(
  args: Omit<CreateItemSelectArgs<T>, 'multiSelect'>
) {
  const component = multiItemSelect({ ...args, multiSelect: false })

  return React.memo(({ item, setItem, ...componentProps }: SingleItemSelectProps<T>) => {
    const items = useMemo(() => (item ? [item] : []), [item])

    const setItems = useCallback<React.Dispatch<React.SetStateAction<T[]>>>(
      (update) => {
        if (typeof update === 'function') {
          setItem((prev) => {
            const next = update(prev ? [prev] : [])
            return next.length ? next[next.length - 1] : null
          })
        } else {
          setItem(update.length ? update[0] : null)
        }
      },
      [setItem]
    )

    return React.createElement(component, { items, setItems, ...componentProps })
  })
}

/**********************************************************************
 * formikConnectedSingleItemSelect
 **********************************************************************/
type FormikConnectedSingleItemSelectArgs<T extends { uuid: string }> = {
  component: React.FunctionComponent<SingleItemSelectProps<T>>
  useItem?: (uuid: string) => T | null | undefined
  mapItem?: (item: T | null | undefined) => any
}

export function formikConnectedSingleItemSelect<T extends { uuid: string }>({
  component,
  useItem = (item) => item as any,
  mapItem = (item) => item,
}: FormikConnectedSingleItemSelectArgs<T>): React.FunctionComponent<
  { name: string; required?: boolean } & Omit<
    SingleItemSelectProps<T>,
    'item' | 'setItem' | 'name' | 'touched' | 'error'
  >
> {
  return ({ name, required, ...selectProps }) => {
    const validate = useCallback(
      (value: any) => (!required || value ? undefined : 'Please fill out this field'),
      [required]
    )

    const [field, meta, helpers] = useField({ name, validate })

    const item = useItem(field.value)

    const handleSelectItem = useCallback(
      (update: React.SetStateAction<T | null | undefined>) => {
        const next = typeof update === 'function' ? update(item) : update
        helpers.setValue(mapItem(next), true)
      },
      [helpers.setValue, item]
    )

    const handleBlur = useCallback(() => field.onBlur(name)({ target: {} }), [name, field.onBlur])

    return React.createElement(component, {
      ...selectProps,
      ...field,
      item,
      setItem: handleSelectItem,
      touched: meta.touched,
      error: meta.error,
      onBlur: handleBlur,
    })
  }
}
